import { Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../../enums/misc.enum';
import { retrieveOnboardingResponseToTab } from '../../../helpers/merchant-onboarding';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import {
  IPaginationMeta,
  OnboardingInformationByTab,
} from '../../../type/admin.types';
import {
  RetrieveOnePartnerAccountsRequest,
  RetrieveOnePartnerAccountsResponse,
  RetrieveOnePartnerResponse,
} from '../../../type/entity.types';
import { ErrorResponse } from '../../../type/error-response.types';
import { BasicAccount } from '../../../type/retrieve-account.types';
import BackButton from '../../buttons/back-button.component';
import EntityInfo from './components/entity-info.component';

function PartnerInfo(props: Props) {
  const { setComponentManager, selectedEntity } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState<
    OnboardingInformationByTab | undefined
  >();
  const axiosPrivate = useAxiosPrivate();
  const [retrieveAccountsParams, setRetrieveAccountsParams] =
    useState<RetrieveOnePartnerAccountsRequest>({
      page: 1,
      partnerId: selectedEntity,
    });
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();

  const [userData, setUserData] = useState<BasicAccount[]>();
  const [partnerData, setPartnerData] = useState<RetrieveOnePartnerResponse>();

  const loadAccountsData = async () => {
    try {
      setIsLoading(true);

      const response = await axiosPrivate.post(
        `/web/partners/accounts`,
        retrieveAccountsParams
      );

      const data: RetrieveOnePartnerAccountsResponse = response.data;

      setUserData(data.items);
      setPaginationData(data.metadata);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-partner-accounts',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsAccountsLoading(false);
    }
  };

  useEffect(() => {
    const loadOnboardingData = async () => {
      try {
        setIsLoading(true);

        const response = await axiosPrivate.get(
          `/web/partners/${selectedEntity}`
        );
        const partnerData: RetrieveOnePartnerResponse = response.data;
        if (partnerData.onboarding) {
          const tabOnboardingData = retrieveOnboardingResponseToTab(
            partnerData.onboarding,
            partnerData.onboarding.merchantId
          );

          setOnboardingData(tabOnboardingData);
        }

        setPartnerData(partnerData);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-partner-onboarding',
          message:
            error?.error ||
            err?.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadOnboardingData();
    loadAccountsData();
  }, []);

  const paginationRequest = (page: number) => {
    setRetrieveAccountsParams((prev) => {
      return {
        ...prev,
        page,
      };
    });
  };

  return (
    <>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
        }}
      />
      <Title order={2} my='lg'>
        View Partner Information
      </Title>
      <EntityInfo
        isEntityActive={partnerData?.isActive}
        disabledReason={partnerData?.disabledReason}
        paginationRequest={paginationRequest}
        loadAccountsData={loadAccountsData}
        onboardingData={onboardingData}
        isLoading={isLoading}
        paginationData={paginationData}
        isAccountsLoading={isAccountsLoading}
        userData={userData}
      />
    </>
  );
}

export default PartnerInfo;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedEntity: number;
}
